import React, { useState, useRef, useEffect } from 'react';
import { format, addHours } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  Slider,
  Alert,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { Iconify } from 'src/components/iconify';
import { generateQrCode } from 'src/api/qr-codes.api';
import { validateTtlHours, validateLocationRange } from 'src/utils/validation-utils';
import { getUserIdFromToken } from 'src/utils/jwt';
import { QrCodeExportPanel } from './QrCodeExportPanel';
import type { QrCodeMetadata } from 'src/types/qr-codes';
import type { BranchDto } from 'src/api/branch_api_data';

// ----------------------------------------------------------------------

interface QrCodeGenerationDialogProps {
  open: boolean;
  onClose: () => void;
  branch: BranchDto;
  onSuccess?: () => void;
}

// TTL Presets configuration
const TTL_PRESETS = [
  { value: 0, label: 'Sınırsız', description: 'Süresiz geçerli' },
  { value: 24, label: '1 Gün', description: '24 saat' },
  { value: 168, label: '1 Hafta', description: '7 gün' },
  { value: 336, label: '2 Hafta', description: '14 gün' },
  { value: 720, label: '1 Ay', description: '30 gün' },
  { value: 2160, label: '3 Ay', description: '90 gün' },
  { value: 8760, label: '1 Yıl', description: '365 gün' },
  { value: -1, label: 'Özel', description: 'Manuel giriş' },
] as const;

// ----------------------------------------------------------------------

/**
 * QR Code Generation Dialog Component
 * Allows admin to generate QR codes for branch check-ins with configurable settings
 */
export function QrCodeGenerationDialog({
  open,
  onClose,
  branch,
  onSuccess,
}: QrCodeGenerationDialogProps) {
  // Form state
  const [ttlHours, setTtlHours] = useState<number>(0);
  const [selectedPreset, setSelectedPreset] = useState<number>(0);
  const [showCustomTtl, setShowCustomTtl] = useState(false);
  const [locationRange, setLocationRange] = useState<number>(100);

  // Validation state
  const [ttlError, setTtlError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Generation state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedQr, setGeneratedQr] = useState<QrCodeMetadata | null>(null);

  // QR canvas ref for export
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setTtlHours(0);
      setSelectedPreset(0);
      setShowCustomTtl(false);
      setLocationRange(100);
      setTtlError(null);
      setLocationError(null);
      setError(null);
      setGeneratedQr(null);
    }
  }, [open]);

  const handlePresetChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: number | null
  ) => {
    if (newValue === null) return;
    
    setSelectedPreset(newValue);
    
    if (newValue === -1) {
      // Custom option selected
      setShowCustomTtl(true);
      setTtlHours(24); // Default custom value
      setTtlError(null);
    } else {
      // Preset selected
      setShowCustomTtl(false);
      setTtlHours(newValue);
      setTtlError(null);
    }
  };

  const handleCustomTtlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? '' : Number(event.target.value);
    setTtlHours(value as number);
    setTtlError(validateTtlHours(value));
  };

  const handleLocationRangeChange = (
    event: Event | React.ChangeEvent<HTMLInputElement>,
    value: number | number[]
  ) => {
    const numValue = typeof value === 'number' ? value : value[0];
    setLocationRange(numValue);
    setLocationError(validateLocationRange(numValue));
  };

  const handleLocationInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? '' : Number(event.target.value);
    setLocationRange(value as number);
    setLocationError(validateLocationRange(value));
  };

  const isFormValid = () => {
    const ttlValidation = validateTtlHours(ttlHours);
    const locationValidation = validateLocationRange(locationRange);
    return ttlValidation === null && locationValidation === null;
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);

      const userId = getUserIdFromToken();
      if (!userId) {
        throw new Error('Kullanıcı oturumu bulunamadı. Lütfen tekrar giriş yapın.');
      }

      const metadata = await generateQrCode(branch.id, {
        ttlHours,
        locationRangeMeters: locationRange,
      });

      // Merge branch data with API response
      const enrichedMetadata: QrCodeMetadata = {
        ...metadata,
        branchAddress: branch.address || '',
        latitude: branch.latitude || 0,
        longitude: branch.longitude || 0,
      };

      setGeneratedQr(enrichedMetadata);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'QR kod oluşturulamadı';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setGeneratedQr(null);
    onClose();
  };

  const getExpirationPreview = () => {
    if (ttlHours === 0) {
      return 'Süresiz';
    }
    const expirationDate = addHours(new Date(), ttlHours);
    return format(expirationDate, 'dd/MM/yyyy HH:mm');
  };

  const formatCoordinate = (value: number | undefined | null): string => {
    if (value === undefined || value === null) {
      return '-';
    }
    return value.toString();
  };

  const getQrCodeDataUrl = (): string => {
    if (!canvasRef.current) {
      console.error('Canvas ref is null');
      return '';
    }

    try {
      // Ensure we get the actual canvas element from the QRCodeCanvas component
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png', 1.0);

      // Validate the data URL
      if (!dataUrl || !dataUrl.startsWith('data:image/png;base64,')) {
        console.error('Invalid data URL format:', dataUrl?.substring(0, 50));
        return '';
      }

      return dataUrl;
    } catch (error) {
      console.error('Error getting canvas data URL:', error);
      return '';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon="eva:qr-code-outline" width={24} />
          <Typography variant="h6">QR Kod Oluştur</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Branch Information (Read-only) */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Şube Bilgileri
            </Typography>
            <Stack spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Şube Adı"
                value={branch.name}
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                size="small"
                label="Adres"
                value={branch.address || '-'}
                InputProps={{ readOnly: true }}
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Enlem"
                  value={formatCoordinate(branch.latitude)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Boylam"
                  value={formatCoordinate(branch.longitude)}
                  InputProps={{ readOnly: true }}
                />
              </Stack>
            </Stack>
          </Box>

          <Divider />

          {/* QR Code Settings */}
          {!generatedQr && (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  QR Kod Ayarları
                </Typography>
                <Stack spacing={2}>
                  {/* TTL Presets */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Geçerlilik Süresi
                    </Typography>
                    <ToggleButtonGroup
                      value={selectedPreset}
                      exclusive
                      onChange={handlePresetChange}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        '& .MuiToggleButtonGroup-grouped': {
                          borderRadius: '8px !important',
                          border: '1px solid',
                          borderColor: 'divider',
                          m: 0.5,
                          '&:not(:first-of-type)': {
                            borderLeft: '1px solid',
                            borderLeftColor: 'divider',
                          },
                        },
                      }}
                    >
                      {TTL_PRESETS.map((preset) => (
                        <ToggleButton
                          key={preset.value}
                          value={preset.value}
                          sx={{
                            px: 2,
                            py: 1,
                            textTransform: 'none',
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                            },
                          }}
                        >
                          <Stack alignItems="center" spacing={0}>
                            <Typography variant="body2" fontWeight="medium">
                              {preset.label}
                            </Typography>
                            <Typography variant="caption" color="inherit" sx={{ opacity: 0.8 }}>
                              {preset.description}
                            </Typography>
                          </Stack>
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>

                  {/* Custom TTL Input (shown only when "Özel" is selected) */}
                  {showCustomTtl && (
                    <TextField
                      fullWidth
                      type="number"
                      label="Özel Geçerlilik Süresi (saat)"
                      value={ttlHours}
                      onChange={handleCustomTtlChange}
                      error={Boolean(ttlError)}
                      helperText={
                        ttlError || '1-8760 saat arası (maksimum 1 yıl)'
                      }
                      InputProps={{
                        inputProps: { min: 1, max: 8760, step: 1 },
                      }}
                    />
                  )}

                  {/* Expiration Preview */}
                  <Alert severity="info" icon={<Iconify icon="eva:clock-outline" />}>
                    Son kullanma: <strong>{getExpirationPreview()}</strong>
                  </Alert>

                  {/* Location Range with Slider */}
                  <Box>
                    <TextField
                      fullWidth
                      type="number"
                      label="Konum Aralığı (metre)"
                      value={locationRange}
                      onChange={handleLocationInputChange}
                      error={Boolean(locationError)}
                      helperText={locationError || '50-500 metre arası geçerlidir'}
                      InputProps={{
                        inputProps: { min: 50, max: 500, step: 10 },
                      }}
                    />
                    <Slider
                      value={locationRange}
                      onChange={handleLocationRangeChange}
                      min={50}
                      max={500}
                      step={10}
                      marks={[
                        { value: 50, label: '50m' },
                        { value: 250, label: '250m' },
                        { value: 500, label: '500m' },
                      ]}
                      valueLabelDisplay="auto"
                      sx={{ mt: 2 }}
                    />
                  </Box>
                </Stack>
              </Box>
            </>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Generated QR Code Display */}
          {generatedQr && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                QR Kod Önizleme
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 3,
                  bgcolor: 'background.neutral',
                  borderRadius: 1,
                }}
              >
                <QRCodeCanvas
                  ref={canvasRef}
                  value={generatedQr.encryptedQrCode}
                  size={256}
                  level="M"
                  includeMargin
                />
              </Box>

              {/* Export Panel */}
              <QrCodeExportPanel metadata={generatedQr} qrCodeDataUrl={getQrCodeDataUrl()} />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {generatedQr ? 'Kapat' : 'İptal'}
        </Button>
        {!generatedQr && (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={!isFormValid() || loading}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : <Iconify icon="eva:plus-fill" />
            }
          >
            Oluştur
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
